import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  message: string;
  timestamp: string;
  messageType: 'text' | 'image' | 'file';
  fileUrl?: string;
  fileName?: string;
}

interface ChatMember {
  id: string;
  name: string;
  avatar?: string;
  role: 'member' | 'treasurer' | 'chairperson';
  isOnline: boolean;
  lastSeen?: string;
}

const ChamaGroupChat: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const colors = getThemeColors('dark');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [members, setMembers] = useState<ChatMember[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (chamaId) {
      loadChatData();
      loadMembers();
    }
  }, [chamaId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadChatData = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(`${API_ENDPOINTS.CHAMA_DETAILS(chamaId)}/chat/messages`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setMessages(data.data);
        } else {
          setMessages([]);
        }
      }
    } catch (error) {
      console.error('Failed to load chat data:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(API_ENDPOINTS.CHAMA_MEMBERS(chamaId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const chatMembers: ChatMember[] = data.data.map((member: any) => ({
            id: member.id,
            name: member.first_name && member.last_name
              ? `${member.first_name} ${member.last_name}`
              : member.name || `Member ${member.id.slice(-4)}`,
            avatar: member.avatar_url || member.avatar,
            role: member.role || 'member',
            isOnline: Math.random() > 0.5, // Mock online status
            lastSeen: new Date(Date.now() - Math.random() * 86400000).toISOString(), // Mock last seen
          }));
          setMembers(chatMembers);
        }
      }
    } catch (error) {
      console.error('Failed to load members:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const messageData = {
        chamaId: chamaId,
        message: newMessage.trim(),
        messageType: 'text' as const,
      };

      const response = await fetch(`${API_ENDPOINTS.CHAMA_DETAILS(chamaId)}/chat/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messageData),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setNewMessage('');
          loadChatData(); // Refresh messages
        } else {
          alert(`Failed to send message: ${data.error || 'Unknown error'}`);
        }
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Send message error:', error);
      alert('Network error: Unable to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const renderMessage = (message: ChatMessage, index: number) => {
    const isCurrentUser = message.senderId === 'current-user'; // Replace with actual user ID check
    const showAvatar = index === 0 || messages[index - 1].senderId !== message.senderId;

    return (
      <div
        key={message.id}
        style={{
          ...styles.messageContainer,
          justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
        }}
      >
        {!isCurrentUser && showAvatar && (
          <div style={styles.messageAvatar}>
            {message.senderAvatar ? (
              <img src={message.senderAvatar} alt={message.senderName} style={styles.avatarImage} />
            ) : (
              <span style={{ fontSize: '14px' }}>
                {message.senderName.split(' ').map(n => n[0]).join('').toUpperCase()}
              </span>
            )}
          </div>
        )}

        <div style={styles.messageContent}>
          {!isCurrentUser && showAvatar && (
            <div style={{ ...styles.messageSender, color: colors.textSecondary }}>
              {message.senderName}
            </div>
          )}

          <div
            style={{
              ...styles.messageBubble,
              backgroundColor: isCurrentUser ? colors.primary : colors.surface,
              border: `1px solid ${colors.border}`,
              alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
            }}
          >
            {message.messageType === 'text' && (
              <div style={{ ...styles.messageText, color: colors.text }}>
                {message.message}
              </div>
            )}

            {message.messageType === 'image' && message.fileUrl && (
              <div style={styles.messageImage}>
                <img src={message.fileUrl} alt="Shared image" style={styles.imageContent} />
              </div>
            )}

            {message.messageType === 'file' && message.fileUrl && (
              <div style={styles.messageFile}>
                <div style={styles.fileIcon}>📎</div>
                <div style={styles.fileInfo}>
                  <div style={{ ...styles.fileName, color: colors.text }}>{message.fileName}</div>
                  <a
                    href={message.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ ...styles.fileDownload, color: colors.primary }}
                  >
                    Download
                  </a>
                </div>
              </div>
            )}
          </div>

          <div style={{ ...styles.messageTime, color: colors.textSecondary }}>
            {formatTime(message.timestamp)}
          </div>
        </div>
      </div>
    );
  };

  const renderMember = (member: ChatMember) => (
    <div key={member.id} style={styles.memberItem}>
      <div style={styles.memberAvatar}>
        {member.avatar ? (
          <img src={member.avatar} alt={member.name} style={styles.avatarImage} />
        ) : (
          <span style={{ fontSize: '14px' }}>
            {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
          </span>
        )}
      </div>
      <div style={styles.memberInfo}>
        <div style={{ ...styles.memberName, color: colors.text }}>{member.name}</div>
        <div style={{ ...styles.memberRole, color: colors.textSecondary }}>
          {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
        </div>
      </div>
      <div style={{ ...styles.memberStatus, color: member.isOnline ? colors.success : colors.textSecondary }}>
        {member.isOnline ? '● Online' : '○ Offline'}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading group chat...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Group Chat"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.chatContainer}>
        {/* Chat Header */}
        <div style={{ ...styles.chatHeader, backgroundColor: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
          <div style={styles.chatInfo}>
            <div style={styles.chatIcon}>💬</div>
            <div style={styles.chatDetails}>
              <div style={{ ...styles.chatName, color: colors.text }}>Group Chat</div>
              <div style={{ ...styles.chatMembers, color: colors.textSecondary }}>
                {members.length} members
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowMembers(!showMembers)}
            style={{ ...styles.membersButton, color: colors.primary }}
          >
            👥
          </button>
        </div>

        {/* Members Panel */}
        {showMembers && (
          <div style={{ ...styles.membersPanel, backgroundColor: colors.surface, borderRight: `1px solid ${colors.border}` }}>
            <div style={styles.membersHeader}>
              <div style={{ ...styles.membersTitle, color: colors.text }}>Members ({members.length})</div>
              <button
                onClick={() => setShowMembers(false)}
                style={{ ...styles.closeButton, color: colors.textSecondary }}
              >
                ✕
              </button>
            </div>
            <div style={styles.membersList}>
              {members.map(renderMember)}
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div style={styles.messagesArea}>
          <div style={styles.messagesContainer}>
            {messages.length > 0 ? (
              messages.map(renderMessage)
            ) : (
              <div style={styles.emptyChat}>
                <div style={styles.emptyIcon}>💬</div>
                <div style={{ ...styles.emptyTitle, color: colors.text }}>No messages yet</div>
                <div style={{ ...styles.emptySubtitle, color: colors.textSecondary }}>
                  Start the conversation by sending the first message!
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <div style={{ ...styles.inputContainer, backgroundColor: colors.surface, borderTop: `1px solid ${colors.border}` }}>
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              style={{ ...styles.messageInput, backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}
              disabled={sending}
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || sending}
              style={{
                ...styles.sendButton,
                backgroundColor: (!newMessage.trim() || sending) ? colors.textSecondary : colors.primary,
                cursor: (!newMessage.trim() || sending) ? 'not-allowed' : 'pointer',
              }}
            >
              {sending ? '...' : '📤'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  chatContainer: {
    display: 'flex',
    height: 'calc(100vh - 80px)', // Account for header
  },
  chatHeader: {
    padding: spacing.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'absolute',
    top: 80,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  chatInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  chatIcon: {
    fontSize: '24px',
    marginRight: spacing.md,
  },
  chatDetails: {
    // Container for chat details
  },
  chatName: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
  },
  chatMembers: {
    fontSize: typography.fontSize.sm,
  },
  membersButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
  },
  membersPanel: {
    width: 300,
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid rgba(255, 255, 255, 0.1)',
  },
  membersHeader: {
    padding: spacing.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  membersTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
  },
  membersList: {
    flex: 1,
    overflowY: 'auto',
    padding: spacing.sm,
  },
  memberItem: {
    display: 'flex',
    alignItems: 'center',
    padding: spacing.sm,
    borderRadius: borderRadius.md,
    marginBottom: spacing.xs,
  },
  memberAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
    objectFit: 'cover',
  },
  memberInfo: {
    flex: 1,
  },
  memberName: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  memberRole: {
    fontSize: typography.fontSize.xs,
  },
  memberStatus: {
    fontSize: typography.fontSize.xs,
  },
  messagesArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: spacing.md,
    paddingTop: 120, // Account for header
  },
  messageContainer: {
    display: 'flex',
    marginBottom: spacing.md,
    alignItems: 'flex-start',
  },
  messageAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(33, 150, 243, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
  },
  messageContent: {
    maxWidth: '70%',
  },
  messageSender: {
    fontSize: typography.fontSize.xs,
    marginBottom: spacing.xs / 2,
  },
  messageBubble: {
    padding: spacing.sm,
    borderRadius: borderRadius.lg,
    maxWidth: '100%',
    wordWrap: 'break-word',
  },
  messageText: {
    fontSize: typography.fontSize.base,
  },
  messageImage: {
    maxWidth: 200,
    maxHeight: 200,
  },
  imageContent: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: borderRadius.md,
  },
  messageFile: {
    display: 'flex',
    alignItems: 'center',
  },
  fileIcon: {
    fontSize: '20px',
    marginRight: spacing.sm,
  },
  fileInfo: {
    flex: 1,
  },
  fileName: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  fileDownload: {
    fontSize: typography.fontSize.xs,
    textDecoration: 'none',
  },
  messageTime: {
    fontSize: typography.fontSize.xs,
    marginTop: spacing.xs / 2,
  },
  emptyChat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: spacing.md,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    fontSize: typography.fontSize.base,
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: spacing.md,
  },
  messageInput: {
    flex: 1,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    fontSize: typography.fontSize.base,
    outline: 'none',
    marginRight: spacing.sm,
  },
  sendButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    border: 'none',
    color: 'white',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

export default ChamaGroupChat;